
import colors from 'lib/constants/resourceColors';
import moment from 'moment';
import Privileges from 'lib/constants/privileges';
import api from 'api';
import { datesForStartHour, datesForWorkHours, endForStartTime } from 'components/Calendar/helpers/workHoursSchedule';
import { saveChangesSchedule, technicianRemovalWarning } from 'components/Calendar/helpers/technicianRemoval';

// Read at save time: technicians assigned since the page loaded aren't in the block's values.
const currentTechnicians = async (block) => {
    try {
        const { status, body } = await api.get(`/inspection_workers?inspection_time_block_id=${block.id}`);
        if(status === 200) {
            return body;
        }
    } catch (e) {
        console.error('Could not read current technicians', e);
    }
    return block.technicians || [];
};

const form = [

    {key: 'scheduled_date',  
        required: true,
        label: 'Start Date', 
        placeholder: 'Select a Start Date', 
        type: 'date', 
        end_date_key: 'scheduled_end_date', 
        time_key: 'start_time',
        onDatePicked: (picked, form, values) => {
            if(!values || !values.work_hours) {
                return;
            }
            const { start, end } = datesForWorkHours(picked, {
                ...values,
                include_breaks: form.getFieldValue('include_breaks'),
            });
            form.setFieldsValue({
                start_time: start,
                scheduled_end_date: end,
                end_time: end,
            });
        },
    },

    {key: 'scheduled_end_date', 
        required: true,
        label: 'End Date', 
        placeholder: 'Select the End Date', 
        type: 'date', 
        start_date_key:'scheduled_date',
        time_key: 'end_time',
        confirmSave: async (changes, values) => saveChangesSchedule(changes)
            ? technicianRemovalWarning(await currentTechnicians(values))
            : null,
    },

    {key: 'start_time',  
        required: true,
        label: 'Start Time', 
        type: 'time',
        date_key: 'scheduled_date',
        defaultValue: moment(new Date()).hour(8).minutes(0).seconds(0),
        quickTimes: [
            {label: '8 AM', hour: 8},
            {label: '10 AM', hour: 10},
            {label: '1 PM', hour: 13},
            {label: '3 PM', hour: 15},
        ],
        quickTimeBlockedReason: (form, values) => {
            if(!values || !values.work_hours) {
                return 'This call has no hours';
            }
            return form.getFieldValue('scheduled_date') ? null : 'Pick a start date first';
        },
        // setStartTime is the input's own onChange: Save stays disabled until a field reports a
        // change, and setFieldsValue alone doesn't count as one.
        onQuickTime: ({hour}, form, values, setStartTime) => {
            const { start, end } = datesForStartHour(form.getFieldValue('scheduled_date'), hour, {
                ...values,
                include_breaks: form.getFieldValue('include_breaks'),
            });
            setStartTime(start);
            form.setFieldsValue({
                scheduled_end_date: end,
                end_time: end,
            });
        },
    },

    {key: 'end_time', 
        required: true,
        label: 'End Time', 
        type: 'time',
        date_key: 'scheduled_end_date',
        defaultValue: moment(new Date()).hour(17).minutes(0).seconds(0),
    },

    {key: 'include_breaks',
        label: 'Includes Break',
        type: 'boolean',
        onToggled: (include_breaks, form, values) => {
            const scheduled_date = form.getFieldValue('scheduled_date');
            const start_time = form.getFieldValue('start_time');
            if(!values || !values.work_hours || !scheduled_date || !start_time) {
                return;
            }
            const end = endForStartTime(scheduled_date, start_time, {...values, include_breaks});
            form.setFieldsValue({
                scheduled_end_date: end,
                end_time: end,
            });
        },
    },

    {key: 'technicians',
        type: 'relations',
        label: 'Technicians',
        placeholder: 'Add a Technician...',
        submitLabel: 'Assign',
        addRelation: (user_id, time_block) => api.create('/inspection_workers', {user_id, inspection_time_block_id: time_block.id}),
            deleteRelation: (user_id, time_block) => api.destroy(`/inspection_workers/time_blocks/${time_block.id}/workers/${user_id}`),
        relationsUrl: (time_block) => `/inspection_workers?inspection_time_block_id=${time_block.id}`,
        optionsUrl: (time_block) => `/lists/inspection_workers_available?inspection_time_block_id=${time_block.id}`,
        getRelations: (block) => { return block.technicians },
        optionTransform: (o) => { 
            return {value: o.id, label: [o.first_name, o.last_name].join(' ')} 
        }, 
        relationTransform: (r) => { 
            return {value: r.user_id, label: [r.first_name, r.last_name].join(' ')} 
        } , 
    },

]

export default {
    defaults: {
        name: 'Inspection Time Block',
        colors: colors.inspectionTimeBlock,
        formFields: form,
        actions: ['view'],
        formActions: [
            { label: 'Shift to 8:00 AM Start Time',
                icon: 'double-left',
                validate: ({values}) => {
                    return values.scheduled_date !== null && values.scheduled_date !== undefined; 
                },
                method: async ({values, reload}) => { 
                    let newTime = new Date(values.scheduled_date);
                    newTime.setHours(8);
                    try { 
                        await api.rpc(`moveBlockToStartTime`, {inspection_time_block_id: values.id, new_start_time: newTime})
                    } catch (e) {
                        throw new Error(e);
                    }
                    reload();
                }
            },
            { label: 'Shift to 1:00 PM Start Time',
                icon: 'double-right',
                validate: ({values}) => {
                    return values.scheduled_date !== null && values.scheduled_date !== undefined; 
                },
                method: async ({values, reload}) => { 
                    let newTime = new Date(values.scheduled_date);
                    newTime.setHours(13);
                console.log("Shift to ", values.id, newTime);
                    try { 
                        await api.rpc(`moveBlockToStartTime`, {inspection_time_block_id: values.id, new_start_time: newTime})
                    } catch (e) {
                        throw new Error(e);
                    }
                    reload();
                }
            },
        ],
    },
    
    view: {
        title: (t) => `Time`,
        formProps: {
            editPrivileges: [Privileges.UPDATE_INSPECTION_TIME_BLOCKS],
            deletePrivileges: [Privileges.DELETE_INSPECTION_TIME_BLOCKS],
        }
    },
}
